import React, { useMemo, useRef, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import styles from './optionsChain.module.css';

import { selectors } from 'state';
import { formatters } from 'services';
import { TraineeSessionContext } from '../TraineeSessionContext';
import { TraineeOptionsContext } from '../TraineeOptionsContext';
import { OptionsContractType } from '../../../state/app/options/types';
import { setSelectedOptionsContract } from '../../../state/app/options/actions';

interface OptionsChainTableProps {
  asset_id: number;
  expiration: string;
}

export interface OptionsChainRow {
  asset_id: number;
  strike: number;
  call?: OptionsContractType;
  put?: OptionsContractType;
}

const OptionsChain: React.FC<OptionsChainTableProps> = ({
  asset_id,
  expiration,
}) => {
  const dispatch = useDispatch();
  const optionsContracts: OptionsContractType[] = useSelector(
    selectors.assetOptionsContracts(asset_id)
  );

  const optionsContext = useContext(TraineeOptionsContext);

  const underlyingSpot = useSelector(selectors.underlyingSpot(asset_id));
  const selectedOptionsContract = useSelector(
    selectors.getSelectedOptionsContract
  );

  // Group options by strike filtering by expiration.
  const chainRows: OptionsChainRow[] = useMemo(() => {
    // TODO: this data filter (similar to TermStructure.tsx data filter) should be
    //  externalized to a common hook
    let tickSize = 10;
    if (underlyingSpot) {
      if (underlyingSpot <= 10) tickSize = 1;
      else if (underlyingSpot <= 25) tickSize = 5;
      else if (underlyingSpot <= 100) tickSize = 10;
      else if (underlyingSpot <= 250) tickSize = 25;
      else if (underlyingSpot <= 500) tickSize = 50;
      /* up to 1000 or above */ else tickSize = 100;
    }

    const grouped: { [strike: number]: OptionsChainRow } = {};

    optionsContracts.forEach(option => {
      // Only include options with the matching expiration date.
      if (option.expiration !== expiration) {
        return;
      }

      // if contract doesn't satisfy correct tick size, don't show it
      if (!Number.isInteger(option.strike / tickSize)) return;

      if (!grouped[option.strike]) {
        grouped[option.strike] = { strike: option.strike, asset_id: asset_id };
      }

      if (option.type_int === 0) {
        // For call options, assign a shallow copy.
        grouped[option.strike].call = { ...option };
      } else if (option.type_int === 1) {
        // For put options, assign a shallow copy.
        grouped[option.strike].put = { ...option };
      }
    });

    // filter out groups lacking implied_volatility on both sides
    const filteredRows = Object.values(grouped).filter(
      row =>
        row.call?.implied_volatility != null ||
        row.put?.implied_volatility != null
    );

    // Fill in missing implied_volatility from the counterpart side
    filteredRows.forEach(row => {
      if (
        row.call &&
        row.call.implied_volatility == null &&
        row.put?.implied_volatility != null
      ) {
        row.call.implied_volatility = row.put.implied_volatility;
      }
      if (
        row.put &&
        row.put.implied_volatility == null &&
        row.call?.implied_volatility != null
      ) {
        row.put.implied_volatility = row.call.implied_volatility;
      }
    });

    return filteredRows.sort((a, b) => a.strike - b.strike);
  }, [optionsContracts, expiration]);

  // Compute the row closest to underlyingSpot (i.e., ATM row)
  const closestRow = useMemo(() => {
    if (underlyingSpot === undefined || chainRows.length === 0) return null;
    return chainRows.reduce(
      (prev, curr) =>
        Math.abs(curr.strike - underlyingSpot) <
        Math.abs(prev.strike - underlyingSpot)
          ? curr
          : prev,
      chainRows[0]
    );
  }, [chainRows, underlyingSpot]);
  // console.log(asset_id);
  // console.log(underlyingSpot);

  // When the component rerenders (e.g. expiration changes), update the selected options contract
  useEffect(() => {
    if (closestRow && selectedOptionsContract === undefined) {
      dispatch(
        setSelectedOptionsContract({ selectedOptionsContract: closestRow })
      );
      // optionsContext.setSelectedOptionsContract(closestRow);
    }
    // if (
    //   closestRow &&
    //   optionsContext.selectedOptionsContract?.strike !== undefined
    // ) {
    //   let matchedStrike = false;
    //   chainRows.forEach(row => {
    //     if (row.strike === optionsContext.selectedOptionsContract?.strike) {
    //       optionsContext.setSelectedOptionsContract(row);
    //       matchedStrike = true;
    //     }
    //     if (!matchedStrike) {
    //       optionsContext.setSelectedOptionsContract(closestRow);
    //     }
    //   });
    // }
  }, [closestRow]);
  useEffect(() => {
    // when user changes asset, reset selection to new closestRow
    // but prevent update when navigating away and back to this component
    if (closestRow !== null && selectedOptionsContract !== undefined) {
      if (selectedOptionsContract.asset_id !== asset_id) {
        dispatch(
          setSelectedOptionsContract({ selectedOptionsContract: closestRow })
        );
      }
    }
  }, [asset_id]);

  useEffect(() => {
    // when expiration changes but asset stays the same,
    // find matching contract with new expiration with the strike of the contract with old expirations
    if (selectedOptionsContract !== undefined) {
      if (
        selectedOptionsContract.put?.expiration !== expiration &&
        selectedOptionsContract.asset_id === asset_id
      ) {
        const newRow = chainRows.find(
          item => item.strike === selectedOptionsContract.strike
        );
        if (newRow !== undefined) {
          dispatch(
            setSelectedOptionsContract({ selectedOptionsContract: newRow })
          );
        } else {
          // if strike doesn't exist in contracts w/ new expiration, select closestRow
          dispatch(
            setSelectedOptionsContract({ selectedOptionsContract: closestRow })
          );
        }
      }
    }
  }, [expiration]);

  // Handler to "select" a row when Trade button is clicked.
  const handleTrade = (row: OptionsChainRow) => {
    dispatch(setSelectedOptionsContract({ selectedOptionsContract: row }));
    // optionsContext.setSelectedOptionsContract(row);
  };

  // Define table columns including the Action column.
  const columns: ColumnsType<OptionsChainRow> = [
    {
      title: 'Select',
      key: 'action',
      render: (_, record) => (
        <Button
          ghost={
            !selectedOptionsContract ||
            selectedOptionsContract.strike !== record.strike
          }
          type={
            selectedOptionsContract &&
            selectedOptionsContract.strike === record.strike
              ? 'primary'
              : 'default'
          }
          onClick={() => handleTrade(record)}
        >
          <div style={{ width: '8px' }}></div>
        </Button>
      ),
      align: 'center',
    },
    {
      title: 'Delta',
      dataIndex: ['call', 'delta'],
      key: 'call_delta',
      render: (_, record) => (record.call ? record.call.delta : '-'),
    },
    {
      title: 'Volume',
      dataIndex: ['call', 'volume'],
      key: 'call_volume',
      render: (_, record) => (record.call ? record.call.volume : '-'),
    },
    {
      title: 'IV',
      dataIndex: ['call', 'implied_volatility'],
      key: 'call_implied_volatility',
      render: (_, record) =>
        record.call
          ? formatters.percent2Digit(record.call.implied_volatility)
          : '-',
    },
    {
      title: 'Bid',
      dataIndex: ['call', 'bid'],
      key: 'call_bid',
      render: (_, record) => (record.call ? record.call.bid : '-'),
    },
    {
      title: 'Ask',
      dataIndex: ['call', 'ask'],
      key: 'call_ask',
      render: (_, record) => (record.call ? record.call.ask : '-'),
    },
    {
      title: 'Interest',
      dataIndex: ['call', 'open_interest'],
      key: 'call_open_interest',
      render: (_, record) => (record.call ? record.call.open_interest : '-'),
    },
    {
      title: 'Strike',
      dataIndex: 'strike',
      key: 'strike',
      align: 'center',
      render: (val, record) => <Typography.Text strong>{val}</Typography.Text>,
    },
    {
      title: '% ATM',
      key: 'percent_atm',
      render: (_, record) => {
        if (underlyingSpot === undefined) {
          return '--';
        } else {
          return record.put
            ? formatters.percent0Digits(record.put.strike / underlyingSpot)
            : '--';
        }
      },
    },
    {
      title: 'Interest',
      dataIndex: ['put', 'open_interest'],
      key: 'put_open_interest',
      render: (_, record) => (record.put ? record.put.open_interest : '-'),
    },
    {
      title: 'Bid',
      dataIndex: ['put', 'bid'],
      key: 'put_bid',
      render: (_, record) => (record.put ? record.put.bid : '-'),
    },
    {
      title: 'Ask',
      dataIndex: ['put', 'ask'],
      key: 'put_ask',
      render: (_, record) => (record.put ? record.put.ask : '-'),
    },
    {
      title: 'IV',
      dataIndex: ['put', 'implied_volatility'],
      key: 'put_implied_volatility',
      // render: (_, record) => (record.put ? record.put.implied_volatility : '-'),
      render: (_, record) => {
        if (record.put) {
          // console.log(record.put.implied_volatility);
          return formatters.percent2Digit(record.put.implied_volatility);
        }
      },
    },
    {
      title: 'Volume',
      dataIndex: ['put', 'volume'],
      key: 'put_volume',
      render: (_, record) => (record.put ? record.put.volume : '-'),
    },
    {
      title: 'Delta',
      dataIndex: ['put', 'delta'],
      key: 'put_delta',
      render: (_, record) => (record.put ? record.put.delta : '-'),
    },
    {
      title: 'QT date',
      dataIndex: ['put', 'quote_date'],
      key: 'put_delta',
      // render: (_, record) => (record.put ? record.put.delta : '-'),
    },
    {
      title: 'EXP',
      dataIndex: ['put', 'expiration'],
      key: 'put_delta',
      // render: (_, record) => (record.put ? record.put.delta : '-'),
    },
  ];

  // Use a ref for the container to adjust scroll if needed.
  const containerRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   if (containerRef.current) {
  //     const tableBody = containerRef.current.querySelector('.ant-table-body');
  //     if (tableBody) {
  //       const bodyEl = tableBody as HTMLElement;
  //       bodyEl.scrollTop = (bodyEl.scrollHeight - bodyEl.clientHeight) / 2;
  //     }
  //   }
  // }, [chainRows]);
  useEffect(() => {
    if (!selectedOptionsContract) return;
    const body =
      containerRef.current?.querySelector<HTMLDivElement>('.ant-table-body');
    if (!body) return;

    const row = body.querySelector<HTMLTableRowElement>(
      `tr[data-row-key="${selectedOptionsContract.strike}"]`
    );
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [chainRows, selectedOptionsContract]);

  return (
    <div
      ref={containerRef}
      id="options-chain-container"
      style={{ scrollBehavior: 'smooth' }}
    >
      <Table
        dataSource={chainRows}
        columns={columns}
        rowKey="strike"
        pagination={false}
        bordered
        scroll={{ y: '25vh' }}
        rowClassName={record => {
          const classes: string[] = [];
          if (closestRow && record.strike === closestRow.strike) {
            classes.push(styles.highlightAtm);
          }
          if (
            selectedOptionsContract &&
            record.strike === selectedOptionsContract.strike
          ) {
            classes.push(styles.highlightSelected);
          }
          return classes.join(' ');
        }}
        footer={() => <div></div>}
      />
    </div>
  );
};

export default OptionsChain;
